import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Users, Euro, Trophy, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { tApi, fileUrl, STATUS_CLS, CATEGORY_LABEL, fmtDate } from "@/lib/tournaments";

import { useSeo } from "@/lib/seo";
export default function TournamentsPage() {
  useSeo({ title: "Beach Volleyball Tournaments in Chania", description: "Beach volley tournaments in Chania, Crete organised by The Sand Lab. Register your team, follow groups, matches and standings.", path: "/tournaments" });
  const [items, setItems] = useState(null);

  useEffect(() => {
    tApi.get("/tournaments").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const upcoming = (items || []).filter((t) => t.status !== "completed");
  const past = (items || []).filter((t) => t.status === "completed");

  return (
    <div className="min-h-screen bg-[#FAF6EF]" data-testid="tournaments-page">
      <Navbar />
      <header className="bg-ocean text-white pt-28 pb-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-sunset mb-3">The Sand Lab</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-none">Tournaments</h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl">
            Διοργανώσεις Beach Volleyball από το The Sand Lab. Δήλωσε συμμετοχή, δες πρόγραμμα, αποτελέσματα και κατάταξη.
          </p>
          <Link to="/rankings" data-testid="tournaments-rankings-link" className="inline-flex items-center gap-2 mt-6 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-2.5 text-sm font-bold">
            <Trophy className="h-4 w-4 text-sunset" /> THE SAND LAB RANKINGS
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-14">
        {items === null && <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-ocean" /></div>}
        {items && items.length === 0 && (
          <div className="text-center py-20 text-slate-500" data-testid="tournaments-empty">Δεν υπάρχουν ανακοινωμένα τουρνουά αυτή τη στιγμή.</div>
        )}
        {upcoming.length > 0 && <Section title="Επερχόμενα & Ενεργά" items={upcoming} testid="tournaments-upcoming" />}
        {past.length > 0 && <Section title="Αρχείο Διοργανώσεων" items={past} testid="tournaments-past" />}
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, items, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="font-heading text-base md:text-lg font-black uppercase tracking-widest text-slate-500 mb-6">{title}</h2>
      <div className="grid gap-8 md:grid-cols-2">
        {items.map((t) => <TournamentCard key={t.id} t={t} />)}
      </div>
    </section>
  );
}

export function TournamentCard({ t }) {
  return (
    <article className="group rounded-3xl bg-white border border-slate-100 shadow-soft overflow-hidden flex flex-col" data-testid={`tournament-card-${t.slug}`}>
      <div className="relative h-52 bg-ocean overflow-hidden">
        {t.cover_url ? (
          <img src={fileUrl(t.cover_url)} alt={t.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="h-full w-full bg-[radial-gradient(circle_at_30%_20%,#1e3a5f,#0b1f3a)] flex items-center justify-center">
            <Trophy className="h-14 w-14 text-sunset/70" />
          </div>
        )}
        <div className="absolute top-4 left-4 flex gap-2">
          <Badge className={`${STATUS_CLS[t.status]} text-[10px] uppercase tracking-widest`} data-testid={`tournament-status-${t.slug}`}>{t.status_label}</Badge>
          <Badge className="bg-white/90 text-ocean text-[10px] uppercase tracking-widest">{CATEGORY_LABEL[t.category]}</Badge>
        </div>
      </div>
      <div className="p-6 flex flex-col gap-4 flex-1">
        <h3 className="font-heading text-xl font-black text-slate-900 leading-tight">{t.title}</h3>
        <p className="text-sm text-slate-600 line-clamp-3">{t.short_description}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm text-slate-700">
          <li className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-sunset shrink-0" />{fmtDate(t.date)}{t.start_time ? ` · ${t.start_time}` : ""}</li>
          <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-sunset shrink-0" />{t.location || "—"}</li>
          <li className="flex items-center gap-2"><Users className="h-4 w-4 text-sunset shrink-0" />{t.approved_count}/{t.max_teams} ομάδες</li>
          <li className="flex items-center gap-2"><Euro className="h-4 w-4 text-sunset shrink-0" />{t.fee === "FREE" || !t.fee ? "Δωρεάν συμμετοχή" : t.fee}</li>
        </ul>
        <div className="mt-auto pt-2">
          <Link to={`/tournaments/${t.slug}`} data-testid={`tournament-info-${t.slug}`} className="inline-flex items-center justify-center w-full rounded-full bg-ocean hover:bg-ocean-hover text-white font-bold text-sm h-11 transition-colors">
            ΠΛΗΡΟΦΟΡΙΕΣ
          </Link>
        </div>
      </div>
    </article>
  );
}
